import Navbar from "../components/layout/Navbar3";
import React, { memo, useEffect, useRef, useState, useCallback } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AmbientParticles from "../components/ui/AmbientParticle";
gsap.registerPlugin(ScrollTrigger);

export default function Gallery() {
  const CDN_BASE = process.env.REACT_APP_CDN_BASE;

  const photoURLs = [
    `${CDN_BASE}/1.jpg`,
    `${CDN_BASE}/3.jpg`,
    `${CDN_BASE}/4.jpg`,
    `${CDN_BASE}/5.jpg`,
    `${CDN_BASE}/6.jpg`,
    `${CDN_BASE}/7.jpg`,
    `${CDN_BASE}/8.jpg`,
    `${CDN_BASE}/9.jpg`,
    `${CDN_BASE}/10.jpg`,
    `${CDN_BASE}/11.jpg`,
    `${CDN_BASE}/12.jpg`,
    `${CDN_BASE}/13.jpg`,
  ];

  const headerRef = useRef(null);
  const subRef = useRef(null);
  const gridRef = useRef(null);
  const blob1Ref = useRef(null);
  const blob2Ref = useRef(null);
  const blob3Ref = useRef(null);

  // --- PERFORMANCE FIX: RAF-throttled mousemove ---
  // Prevents 144 DOM writes/second. Only writes once per frame.
  const mouseTicking = useRef(false);
  const mousePos = useRef({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e) => {
    mousePos.current = {
      x: (e.clientX / window.innerWidth - 0.5) * 2,
      y: (e.clientY / window.innerHeight - 0.5) * 2,
    };
    if (!mouseTicking.current) {
      mouseTicking.current = true;
      requestAnimationFrame(() => {
        const { x, y } = mousePos.current;
        // Use translate3d to force GPU layer compositing
        if (blob1Ref.current) blob1Ref.current.style.transform = `translate3d(${x * 30}px, ${y * 30}px, 0)`;
        if (blob2Ref.current) blob2Ref.current.style.transform = `translate3d(${x * -40}px, ${y * 20}px, 0)`;
        if (blob3Ref.current) blob3Ref.current.style.transform = `translate3d(${x * 20}px, ${y * -30}px, 0)`;
        mouseTicking.current = false;
      });
    }
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power4.out", delay: 0.2 }
      );
      gsap.fromTo(
        subRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.9, ease: "power3.out", delay: 0.4 }
      );

      gsap.fromTo(
        ".gallery-card",
        { y: 60, opacity: 0, scale: 0.95 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.7,
          stagger: 0.08,
          ease: "power3.out",
          scrollTrigger: {
            trigger: gridRef.current,
            start: "top 85%",
          },
        }
      );
    });

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      ctx.revert();
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [handleMouseMove]);

  return (
   <div className="relative min-h-screen bg-slate-950">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:ital,wght@0,400;0,600;1,400;1,600&display=swap');
        .font-playfair { font-family: 'Playfair Display', serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
        .text-gradient-mask {
          background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.65) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .noise-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E");
        }
      `}</style>

      {/* Preload first 4 images so LCP isn't blocked */}
      {photoURLs.slice(0, 4).map((url, i) => (
        <link key={`preload-${i}`} rel="preload" as="image" href={url} />
      ))}

      <div className="fixed inset-0 noise-overlay pointer-events-none z-[1] opacity-30" />
      <AmbientParticles />
      <div ref={blob1Ref} className="fixed -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-600/15 blur-[130px] opacity-60 pointer-events-none z-0 will-change-transform" />
      <div ref={blob2Ref} className="fixed bottom-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-purple-600/15 blur-[110px] opacity-50 pointer-events-none z-0 will-change-transform" />
      <div ref={blob3Ref} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-indigo-600/10 blur-[150px] opacity-40 pointer-events-none z-0 will-change-transform" />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-500/5 rounded-full blur-[150px] pointer-events-none z-0" />
      <Navbar />
      <main className="min-h-screen font-sans pt-16 md:pt-20 relative z-10">
        <section className="container mx-auto p-4 sm:p-6 lg:p-8">
          <header className="text-center mb-14 pt-8">
            <h1 
              ref={headerRef}
              className="font-inter text-4xl md:text-6xl lg:text-7xl font-black tracking-tight text-gradient-mask opacity-0"
            >
              MNR Photo Gallery
            </h1>
            <p 
              ref={subRef}
              className="font-playfair italic text-xl md:text-2xl text-gray-500 mt-4 opacity-0"
            >
              A collection of our favorite moments.
            </p>
          </header>

          <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 pb-24">
            {photoURLs.map((url, index) => (
              <PhotoCard 
                key={index} 
                url={url} 
                alt={`Photo ${index + 1}`} 
                index={index}
              />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

const PhotoCard = memo(({ url, alt, index }) => {
  const fallbackSrc =
    "https://placehold.co/600x400/ef4444/ffffff?text=Image+Unavailable";
  const [isLoaded, setIsLoaded] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // Start loading image
          const img = new Image();
          img.src = url;
          img.decoding = "async"; // Off-main-thread decode
          img.onload = () => setIsLoaded(true);
          img.onerror = () => setIsLoaded(true);
          observer.disconnect();
        }
      },
      { 
        rootMargin: "200px", // Start loading 200px before it enters viewport
        threshold: 0.05 
      }
    );

    if (cardRef.current) observer.observe(cardRef.current);
    return () => {
      if (cardRef.current) observer.unobserve(cardRef.current);
    };
  }, [url]);

  return (
    <figure
      ref={cardRef}
      className="gallery-card group relative overflow-hidden rounded-2xl bg-white/[0.03] border border-white/10 shadow-2xl shadow-black/40 aspect-[4/3] hover:border-white/20 hover:shadow-black/60 transition-all duration-500 will-change-transform"
      role="img"
      aria-label={alt}
    >
      <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500 -z-10" />

      <div className="relative w-full h-full overflow-hidden rounded-2xl">
        <img
          src={isLoaded ? url : ""}
          alt={alt}
          width="400"
          height="300"
          loading={index < 4 ? "eager" : "lazy"}
          decoding="async"
          fetchpriority={index < 4 ? "high" : "low"}
          onError={(e) => (e.currentTarget.src = fallbackSrc)}
          className={`w-full h-full object-cover transition-opacity duration-700 ease-out ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
        />
        
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-slate-950/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
    </figure>
  );
});